import Analytics from '@/utils/analytics';
import { getShortcutTitle } from '@platform/shortcuts/utils';
import { Key } from '@platform/shortcuts/utils/keyboard-platform';
import { TrackingProperties } from '@/utils/analytics/base-reporter';

export enum KeyboardScope {
  ACTION_SHEET = 'ACTION_SHEET',
  GLOBAL = 'GLOBAL',
  LOCATION_PICKER = 'LOCATION_PICKER',
  ORDER_SUMMARY = 'ORDER_SUMMARY',
  SCOPED = 'SCOPED',
}

/**
 * Keyboard shortcuts for Operator Portal
 * Uses 'alt' and 'shift' as modifiers for intentional actions
 * Single-key shortcuts are protected by focus detection
 */
export enum AllowedShortcuts {
  // Modifier shortcuts (intentional actions)
  AddNote = `${Key.Alt}+n`,
  ArrowDown = `${Key.Alt}+${Key.ArrowDown}`,
  ArrowUp = `${Key.Alt}+${Key.ArrowUp}`,
  PlaceOrder = `${Key.Shift}+${Key.Enter}`,
  SwitchExperience = `${Key.Alt}+e`,
  ToggleKeyboardShortcutsModal = `${Key.Alt}+h`,
  ToggleSalesRepMode = `${Key.Alt}+m`,
  ViewRestaurantAccount = `${Key.Alt}+a`,

  // Single-key shortcuts (quick access, protected by focus detection)
  Search = 'Slash',
  ToggleCart = 'c',
  ToggleLocationPicker = 'l',
}

/**
 * Allow number keys alt + ${1-9} to select items from the list
 */
export const AllowedShortcutNumbers = Array.from(
  { length: 9 },
  (_, i) => `${Key.Alt}+${i + 1}`,
);

/**
 * Map of number keys to their respective index
 */
export const numberKeyMap = AllowedShortcutNumbers.reduce(
  (acc, key, index) => {
    acc[key] = index + 1;
    return acc;
  },
  {} as { [key: string]: number },
);

/**
 * Log the keyboard shortcut action
 * Accepts string to support shortcuts from any experience
 * Example event name: keyboard_shortcut.add_note.select
 * @param pressedKey - the key that was pressed
 * @param properties - additional properties to log
 * @returns void
 */
export const logKey = (
  pressedKey: string | string[],
  properties: TrackingProperties,
) => {
  const action = getShortcutTitle(pressedKey);
  const action_name = action.toLowerCase().replaceAll(' ', '_');
  Analytics.track(`keyboard_shortcut.${action_name}`, {
    action,
    key: pressedKey,
    properties,
  });
};
